/**
 * vl-home-media-testimonials.js — 首页「Professional Reviews」视频证言滑块的交互
 *
 * 需求：需求/20260922-新版首页改版/子需求/08-首页ProfessionalReviews视频证言滑块/
 *
 * 职责：
 *   ①胶囊（role=tab）切换分组：点击 + ←/→/Home/End（roving tabindex、aria-selected），切换后该分组轨道归零；
 *   ②轨道横滑：原生 `overflow-x:auto` + `scroll-snap`，无自动播放；
 *   ③**圆点＝「可切换的次数」**（用户 2026-09-30 澄清，不是卡片数）：
 *       位置序列 = [0, step, 2*step, …] 直到「末卡右缘与内容右缘重叠」那一档（末位固定落在 maxScroll）；
 *       圆点与箭头都按这个位置序列走；只有 1 个位置（无可切换）时不渲染圆点并同时禁用两个箭头；
 *   ④播放：点击播放按钮或整卡 → 弹窗播放（YouTube / Vimeo / 直链 mp4；其余按原样 iframe）；
 *   ⑤YouTube 自动封面兜底：`maxresdefault` 不存在时回退 `hqdefault`（maxres 只有高清源才有，
 *      否则 YouTube 会返回灰底占位图 —— 用户 2026-09-30 截图里的灰底即此情形）。
 *
 * 约定（规范 3.6）：IIFE + 只注册一次自定义元素 + `disconnectedCallback` 清理监听。
 */
(function () {
  'use strict';

  if (typeof window === 'undefined' || window.VLHomeMediaTestimonials) return;

  /**
   * YouTube 自动封面降级链：`maxresdefault` → `sddefault` → `hqdefault`。
   * 关键事实（2026-09-30 实测）：源视频没有高清图时，YouTube 对 `maxresdefault` 会返回
   * **HTTP 200 的 120×90 灰底占位图**（不是 404）⇒ **不会触发 `error`**，
   * 因此必须**按加载后的自然宽度判定**（占位图宽 ≤ 200）再降级。
   */
  var YT_COVER_CHAIN = ['maxresdefault.jpg', 'sddefault.jpg', 'hqdefault.jpg'];
  var YT_COVER_PLACEHOLDER_MAX_W = 200;

  function buildPlayerSource(video, youtubeId) {
    if (youtubeId) {
      return { type: 'iframe', src: 'https://www.youtube.com/embed/' + encodeURIComponent(youtubeId) + '?autoplay=1&rel=0' };
    }
    if (!video) return null;
    var value = String(video).trim();
    if (!value) return null;

    var vimeo = value.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
    if (vimeo) {
      return { type: 'iframe', src: 'https://player.vimeo.com/video/' + vimeo[1] + '?autoplay=1' };
    }
    if (/\.(mp4|webm|ogv|ogg|mov)(\?|#|$)/i.test(value)) {
      return { type: 'video', src: value };
    }
    return { type: 'iframe', src: value };
  }

  class VLHomeMediaTestimonials extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;

      this._tabs = Array.prototype.slice.call(this.querySelectorAll('[data-vl-hmt-tab]'));
      this._panels = Array.prototype.slice.call(this.querySelectorAll('[data-vl-hmt-panel]'));
      this._modal = this.querySelector('[data-vl-hmt-modal]');
      this._player = this.querySelector('[data-vl-hmt-modal-player]');
      this._lastFocus = null;

      this._onClick = this._handleClick.bind(this);
      this._onKeydown = this._handleKeydown.bind(this);
      this.addEventListener('click', this._onClick);
      this.addEventListener('keydown', this._onKeydown);

      this._fixYouTubeCovers();
      this._panels.forEach(this._initPanel, this);

      /* 初始化自愈（2026-09-30 回归修复）：以「当前选中的胶囊」为准，强制校正一次面板显隐与
         aria/tabindex。即便服务端标记出现回归（例如多个面板都带 `is-active`），JS 就绪后立即归一；
         正常状态下这里是一次幂等操作，不产生副作用（也不会重置用户已滚动的位置）。 */
      var initial = this._tabs.filter(function (tab) {
        return tab.getAttribute('aria-selected') === 'true' || tab.classList.contains('is-selected');
      })[0] || this._tabs[0];
      if (initial) {
        var initialIndex = parseInt(initial.getAttribute('data-vl-hmt-tab'), 10);
        if (!isNaN(initialIndex)) this._select(initialIndex, { keepScroll: true });
      }

      if (this._modal) {
        this._onDocumentKeydown = function (event) {
          if (event.key === 'Escape' && !this._modal.hidden) this._closeModal();
        }.bind(this);
        document.addEventListener('keydown', this._onDocumentKeydown);
      }
    }

    disconnectedCallback() {
      if (this._onClick) this.removeEventListener('click', this._onClick);
      if (this._onKeydown) this.removeEventListener('keydown', this._onKeydown);
      if (this._onDocumentKeydown) document.removeEventListener('keydown', this._onDocumentKeydown);
      this._panels.forEach(function (panel) {
        if (panel._rail && panel._onScroll) panel._rail.removeEventListener('scroll', panel._onScroll);
        if (panel._onResize) window.removeEventListener('resize', panel._onResize);
      });
      this._bound = false;
    }

    /* ------------------------------------------------------------- 自动封面兜底 */

    _fixYouTubeCovers() {
      Array.prototype.forEach.call(this.querySelectorAll('[data-vl-hmt-ytcover]'), function (img) {
        function nextInChain() {
          var src = img.getAttribute('src') || '';
          for (var i = 0; i < YT_COVER_CHAIN.length - 1; i++) {
            if (src.indexOf(YT_COVER_CHAIN[i]) !== -1) {
              img.dataset.vlHmtYtStep = String(i + 1);
              img.src = src.replace(YT_COVER_CHAIN[i], YT_COVER_CHAIN[i + 1]);
              return true;
            }
          }
          return false;
        }
        function check() {
          if (img.dataset.vlHmtYtDone === '1') return;
          var w = img.naturalWidth || 0;
          // 占位图（宽 ≤ 200）或加载失败（宽 0 且已完成）⇒ 降级一档
          var isPlaceholder = w > 0 && w <= YT_COVER_PLACEHOLDER_MAX_W;
          var isBroken = w === 0 && img.complete && !!img.getAttribute('src');
          if (!isPlaceholder && !isBroken) {
            if (w > 0) img.dataset.vlHmtYtDone = '1';
            return;
          }
          if (!nextInChain()) img.dataset.vlHmtYtDone = '1';
        }
        img.addEventListener('load', check);
        img.addEventListener('error', check);
        if (img.complete) check();
      });
    }

    /* ------------------------------------------------------------- 轨道与控件 */

    _initPanel(panel) {
      var rail = panel.querySelector('[data-vl-hmt-rail]');
      if (!rail) return;
      var self = this;
      panel._rail = rail;
      panel._dots = panel.querySelector('[data-vl-hmt-dots]');
      panel._onScroll = function () {
        if (panel._raf) return;
        panel._raf = window.requestAnimationFrame(function () {
          panel._raf = null;
          self._syncPanel(panel);
        });
      };
      panel._onResize = function () {
        self._buildDots(panel);
        self._syncPanel(panel);
      };
      rail.addEventListener('scroll', panel._onScroll, { passive: true });
      window.addEventListener('resize', panel._onResize, { passive: true });
      this._buildDots(panel);
      this._syncPanel(panel);
    }

    /** 单张卡的步长 = 卡宽 + 间距 */
    _step(rail) {
      var item = rail.querySelector('.vl-hmt__item');
      if (!item) return 0;
      var gap = parseFloat(window.getComputedStyle(rail).columnGap || window.getComputedStyle(rail).gap) || 0;
      return item.getBoundingClientRect().width + gap;
    }

    /**
     * 位置序列：`[0, step, 2*step, …]`，末位固定落在 maxScroll
     * —— 末位即「末卡右缘与内容右缘重叠」的那个位置（无法继续滑动）。
     * 若剩余可滚距离不足「一步的 35%」，不再单独插一个位置（避免出现几乎重叠的两档）。
     */
    _metrics(panel) {
      var rail = panel._rail;
      if (!rail) return null;
      var step = this._step(rail) || 1;
      var maxScroll = Math.max(0, rail.scrollWidth - rail.clientWidth);
      var positions = [0];
      if (maxScroll > 1) {
        var p = 0;
        while (maxScroll - p > step * 0.35) {
          p = Math.min(p + step, maxScroll);
          positions.push(Math.round(p));
          if (p >= maxScroll) break;
        }
      }
      return { step: step, maxScroll: Math.round(maxScroll), positions: positions };
    }

    _buildDots(panel) {
      if (!panel._dots) return;
      var m = this._metrics(panel);
      if (!m) return;
      panel._metrics = m;
      var dots = panel._dots;
      // 只有 1 个位置（无可切换）⇒ 不渲染圆点
      if (m.positions.length <= 1) {
        while (dots.firstChild) dots.removeChild(dots.firstChild);
        dots.hidden = true;
        return;
      }
      dots.hidden = false;
      var existing = dots.querySelectorAll('[data-vl-hmt-dot]').length;
      if (existing === m.positions.length) return;
      while (dots.firstChild) dots.removeChild(dots.firstChild);
      m.positions.forEach(function (_, i) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'vl-hmt__dot' + (i === 0 ? ' is-active' : '');
        btn.setAttribute('data-vl-hmt-dot', String(i));
        btn.setAttribute('aria-label', 'Slide ' + (i + 1) + ' of ' + m.positions.length);
        dots.appendChild(btn);
      });
    }

    _activeIndex(panel) {
      var m = panel._metrics;
      if (!m || !panel._rail) return 0;
      var left = panel._rail.scrollLeft;
      var best = 0;
      var bestDelta = Infinity;
      m.positions.forEach(function (p, i) {
        var d = Math.abs(p - left);
        if (d < bestDelta) { bestDelta = d; best = i; }
      });
      return best;
    }

    _syncPanel(panel) {
      var rail = panel._rail;
      if (!rail) return;
      var m = this._metrics(panel);
      if (!m) return;
      panel._metrics = m;
      if (panel._dots && panel._dots.querySelectorAll('[data-vl-hmt-dot]').length !== m.positions.length) {
        this._buildDots(panel);
        m = panel._metrics;
      }

      var index = this._activeIndex(panel);
      if (panel._dots) {
        Array.prototype.forEach.call(panel._dots.querySelectorAll('[data-vl-hmt-dot]'), function (dot, i) {
          dot.classList.toggle('is-active', i === index);
        });
      }

      var prev = panel.querySelector('[data-vl-hmt-prev]');
      var next = panel.querySelector('[data-vl-hmt-next]');
      var noScroll = m.maxScroll <= 1;
      if (prev) prev.disabled = noScroll || index <= 0;
      if (next) next.disabled = noScroll || index >= m.positions.length - 1;
    }

    _select(index, options) {
      var self = this;
      var keepScroll = !!(options && options.keepScroll);
      this._tabs.forEach(function (tab) {
        var selected = parseInt(tab.getAttribute('data-vl-hmt-tab'), 10) === index;
        tab.classList.toggle('is-selected', selected);
        tab.setAttribute('aria-selected', selected ? 'true' : 'false');
        tab.setAttribute('tabindex', selected ? '0' : '-1');
      });

      this._panels.forEach(function (panel) {
        var active = parseInt(panel.getAttribute('data-vl-hmt-panel'), 10) === index;
        panel.classList.toggle('is-active', active);
        panel.hidden = !active;
        if (active && panel._rail) {
          if (!keepScroll) panel._rail.scrollLeft = 0;
          // 面板由 hidden → 显示后尺寸才可用，需重新量测（圆点数量与视口宽度相关）
          self._buildDots(panel);
          self._syncPanel(panel);
          window.requestAnimationFrame(function () { self._syncPanel(panel); });
        }
      });
    }

    /* ------------------------------------------------------------- 事件 */

    _handleClick(event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;

      var close = target.closest('[data-vl-hmt-modal-close]');
      if (close && this.contains(close)) {
        this._closeModal();
        return;
      }

      var tab = target.closest('[data-vl-hmt-tab]');
      if (tab && this.contains(tab)) {
        this._select(parseInt(tab.getAttribute('data-vl-hmt-tab'), 10));
        return;
      }

      var dot = target.closest('[data-vl-hmt-dot]');
      if (dot && this.contains(dot)) {
        var panel = dot.closest('[data-vl-hmt-panel]');
        var m = panel && panel._metrics;
        if (panel && panel._rail && m) {
          var i = parseInt(dot.getAttribute('data-vl-hmt-dot'), 10);
          var left = m.positions[Math.max(0, Math.min(i, m.positions.length - 1))];
          panel._rail.scrollTo({ left: left, behavior: 'smooth' });
        }
        return;
      }

      var stepButton = target.closest('[data-vl-hmt-prev], [data-vl-hmt-next]');
      if (stepButton && this.contains(stepButton)) {
        var p = stepButton.closest('[data-vl-hmt-panel]');
        var pm = p && p._metrics;
        if (p && p._rail && pm) {
          var current = this._activeIndex(p);
          var next = stepButton.hasAttribute('data-vl-hmt-next') ? current + 1 : current - 1;
          next = Math.max(0, Math.min(next, pm.positions.length - 1));
          p._rail.scrollTo({ left: pm.positions[next], behavior: 'smooth' });
        }
        return;
      }

      var card = target.closest('[data-vl-hmt-card]');
      if (card && card.classList.contains('is-playable')) {
        this._openModal(card);
      }
    }

    _handleKeydown(event) {
      var tab = event.target;
      if (!tab || !tab.hasAttribute || !tab.hasAttribute('data-vl-hmt-tab')) return;

      var count = this._tabs.length;
      if (count < 2) return;
      var current = this._tabs.indexOf(tab);
      if (current < 0) return;

      var next;
      if (event.key === 'ArrowRight') next = (current + 1) % count;
      else if (event.key === 'ArrowLeft') next = (current - 1 + count) % count;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = count - 1;
      else return;

      event.preventDefault();
      this._select(next);
      this._tabs[next].focus();
    }

    /* ------------------------------------------------------------- 播放弹窗 */

    _openModal(card) {
      if (!this._modal || !this._player) return;
      var source = buildPlayerSource(card.getAttribute('data-vl-hmt-video'), card.getAttribute('data-vl-hmt-yt'));
      if (!source) return;

      this._lastFocus = document.activeElement;
      while (this._player.firstChild) this._player.removeChild(this._player.firstChild);

      var node;
      if (source.type === 'video') {
        node = document.createElement('video');
        node.src = source.src;
        node.controls = true;
        node.autoplay = true;
        node.playsInline = true;
      } else {
        node = document.createElement('iframe');
        node.src = source.src;
        node.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture; fullscreen');
        node.setAttribute('allowfullscreen', '');
        node.setAttribute('title', (card.querySelector('.vl-hmt__name') && card.querySelector('.vl-hmt__name').textContent.trim()) || 'Review video');
      }
      this._player.appendChild(node);

      this._modal.hidden = false;
      this._bodyOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';

      var closeButton = this._modal.querySelector('[data-vl-hmt-modal-close]');
      if (closeButton) closeButton.focus();
    }

    _closeModal() {
      if (!this._modal) return;
      this._modal.hidden = true;
      if (this._player) {
        while (this._player.firstChild) this._player.removeChild(this._player.firstChild);
      }
      document.documentElement.style.overflow = this._bodyOverflow || '';
      if (this._lastFocus && typeof this._lastFocus.focus === 'function') this._lastFocus.focus();
      this._lastFocus = null;
    }
  }

  if (!customElements.get('vl-home-media-testimonials')) {
    customElements.define('vl-home-media-testimonials', VLHomeMediaTestimonials);
  }

  window.VLHomeMediaTestimonials = VLHomeMediaTestimonials;
})();
